<template>
  <div class="comentarios-mg">
    <div ref="lista" class="comentarios-mg__lista">
      <div v-if="cargando" class="comentarios-mg__estado">Cargando…</div>

      <div
        v-else-if="error"
        class="comentarios-mg__estado comentarios-mg__estado--error"
      >
        {{ error }}
      </div>

      <div v-else-if="comentarios.length === 0" class="comentarios-mg__estado">
        Todavía no hay comentarios en esta fila.
      </div>

      <template v-else>
        <article
          v-for="comentario in comentarios"
          :key="comentario.id"
          class="comentarios-mg__item"
        >
          <header class="comentarios-mg__cabecera">
            <span class="comentarios-mg__autor">{{ autor(comentario) }}</span>
            <span class="comentarios-mg__fecha">{{
              fecha(comentario.creado)
            }}</span>
            <button
              v-if="esMio(comentario)"
              type="button"
              class="comentarios-mg__borrar"
              @click="pedirBorrado(comentario)"
            >
              {{ confirmando === comentario.id ? '¿Borrar?' : '×' }}
            </button>
          </header>

          <p class="comentarios-mg__mensaje">
            <template v-for="(trozo, i) in trozos(comentario)" :key="i">
              <span v-if="trozo.mencion" class="comentarios-mg__mencion">{{
                trozo.texto
              }}</span>
              <template v-else>{{ trozo.texto }}</template>
            </template>
          </p>

          <div class="comentarios-mg__reacciones">
            <button
              v-for="r in comentario.reacciones"
              :key="r.emoji"
              type="button"
              class="comentarios-mg__reaccion"
              :class="{ 'comentarios-mg__reaccion--mia': r.mio }"
              :title="r.mio ? 'Quitar tu reacción' : 'Reaccionar'"
              @click="alternarReaccion(comentario, r.emoji)"
            >
              <span>{{ r.emoji }}</span>
              <span class="comentarios-mg__reaccion-total">{{ r.total }}</span>
            </button>

            <button
              type="button"
              class="comentarios-mg__reaccion comentarios-mg__reaccion--agregar"
              title="Agregar una reacción"
              @click="paleta = paleta === comentario.id ? null : comentario.id"
            >
              ☺+
            </button>

            <div v-if="paleta === comentario.id" class="comentarios-mg__paleta">
              <button
                v-for="e in EMOJIS"
                :key="e"
                type="button"
                class="comentarios-mg__paleta-boton"
                @click="elegirEmoji(comentario, e)"
              >
                {{ e }}
              </button>
            </div>
          </div>
        </article>
      </template>
    </div>

    <!--
      Avisos de esta fila. Va pegado al hilo y no en los ajustes de la cuenta
      porque la decision se toma AQUI: uno silencia el hilo que se desbordo, en
      el momento en que se desborda, no entrando a una pantalla de preferencias.
    -->
    <button
      type="button"
      class="comentarios-mg__avisos"
      :title="
        modoDeAvisos === 'menciones'
          ? 'Solo te avisamos si te mencionan en esta fila. Clic para recibir todos los comentarios.'
          : 'Te avisamos de cada comentario de esta fila. Clic para recibir solo tus menciones.'
      "
      @click="alternarAvisos"
    >
      <i
        :class="
          modoDeAvisos === 'menciones' ? 'iconoir-bell-off' : 'iconoir-bell'
        "
      ></i>
      {{
        modoDeAvisos === 'menciones'
          ? 'Solo menciones'
          : 'Todos los comentarios'
      }}
    </button>

    <form class="comentarios-mg__form" @submit.prevent="enviar">
      <!--
        El desplegable va ENCIMA del campo y no flotando junto al cursor.
        Flotarlo obliga a medir la posición del caret dentro de un textarea, que
        no se puede hacer sin duplicar el texto en un elemento espejo. Para una
        barra de 250px, una lista aquí se usa igual de bien.
      -->
      <ul v-if="sugerencias.length" class="comentarios-mg__sugerencias">
        <li
          v-for="(m, i) in sugerencias"
          :key="m.id"
          class="comentarios-mg__sugerencia"
          :class="{ 'comentarios-mg__sugerencia--activa': i === indiceActivo }"
          @mousedown.prevent="elegirMencion(m)"
        >
          {{ m.nombre }}
        </li>
      </ul>

      <textarea
        ref="campo"
        v-model="borrador"
        class="comentarios-mg__campo"
        rows="3"
        placeholder="Escribe un comentario… Escribe @ para mencionar a alguien."
        :disabled="enviando"
        @input="alEscribir"
        @keydown="alTeclear"
      ></textarea>
      <Button
        type="primary"
        size="small"
        full-width
        :disabled="!puedeEnviar"
        :loading="enviando"
      >
        Comentar
      </Button>
    </form>
  </div>
</template>

<script>
import Button from '@baserow/modules/core/components/Button.vue'

import ComentariosService, { EMOJIS } from '@comentarios_mg/services/comentarios'

// Una mención viaja guardada como "@{12}", con el id y no con el nombre: si
// alguien se cambia el nombre la mención vieja sigue apuntando a la persona
// correcta, y con dos personas que se llamen igual no hay ambigüedad. El nombre
// se resuelve aquí, al pintar.
const RE_MARCADOR = /@\{(\d+)\}/g

// Lo que se está escribiendo detrás de un "@" al final del texto, hasta el
// cursor. Se permiten espacios porque los nombres los llevan ("Victor
// Vazquez"), pero se corta a tres palabras: sin ese tope, escribir "@" al
// principio de una frase deja el desplegable abierto para siempre.
const RE_CONSULTA = /@([\p{L}\p{N}._-]*(?:[ ][\p{L}\p{N}._-]+){0,2})$/u

export default {
  name: 'ComentariosSidebar',
  components: { Button },
  props: {
    database: { type: Object, required: true },
    table: { type: Object, required: true },
    fields: { type: Array, required: true },
    row: { type: Object, required: true },
    view: { type: Object, required: false, default: null },
  },
  data() {
    return {
      EMOJIS,
      comentarios: [],
      borrador: '',
      cargando: true,
      enviando: false,
      error: null,
      // Id del comentario esperando el segundo clic para borrarse. Evita un
      // window.confirm, que en Baserow bloquea el modal de la fila.
      confirmando: null,
      // Id del comentario cuya paleta de emojis está abierta.
      paleta: null,
      // Quién puede ser mencionado, y a quién se eligió del desplegable.
      miembros: [],
      mencionados: [],
      consulta: null,
      indiceActivo: 0,
      // Como quiere esta persona que le avisen de ESTA fila. Se arranca en
      // 'todos', que es el default del servidor: asi el boton no parpadea
      // mientras llega la respuesta.
      modoDeAvisos: 'todos',
    }
  },
  computed: {
    puedeEnviar() {
      return !this.enviando && this.borrador.trim().length > 0
    },
    usuarioActualId() {
      return this.$store.getters['auth/getUserObject']?.id
    },
    sugerencias() {
      if (this.consulta === null) {
        return []
      }
      const q = this.consulta.toLowerCase()
      return this.miembros
        .filter((m) => (m.nombre || '').toLowerCase().includes(q))
        .slice(0, 6)
    },
  },
  watch: {
    // El modal reutiliza el componente al saltar de fila con las flechas.
    'row.id'(id) {
      // Si la fila desaparece con el modal abierto (la borraron, o quien solo
      // ve sus asuntos se quitó de Personas), no hay nada que pedir.
      if (id === undefined || id === null) return
      this.cargar()
      this.cargarAvisos()
    },
  },
  mounted() {
    this.cargar()
    this.cargarMiembros()
    this.cargarAvisos()
  },
  methods: {
    async cargar() {
      this.cargando = true
      this.error = null
      this.confirmando = null
      this.paleta = null
      const filaPedida = this.row.id
      try {
        const { data } = await ComentariosService(this.$client).lista(
          this.table.id,
          filaPedida
        )
        // Si el usuario ya saltó a otra fila, esta respuesta llegó tarde.
        if (filaPedida !== this.row.id) {
          return
        }
        this.comentarios = data
        this.alFinal()
      } catch (e) {
        this.error = 'No se pudieron cargar los comentarios.'
      } finally {
        this.cargando = false
      }
    },
    async cargarAvisos() {
      // Si falla se queda en el default y ya: el usuario vino a leer
      // comentarios, no a configurar avisos.
      const filaPedida = this.row.id
      try {
        const { data } = await ComentariosService(this.$client).avisos(
          this.table.id,
          filaPedida
        )
        if (filaPedida === this.row.id) {
          this.modoDeAvisos = data.modo
        }
      } catch (e) {
        this.modoDeAvisos = 'todos'
      }
    },
    async alternarAvisos() {
      const nuevo = this.modoDeAvisos === 'menciones' ? 'todos' : 'menciones'
      const anterior = this.modoDeAvisos
      // Se pinta antes de que conteste el servidor y se revierte si falla: es
      // un interruptor, y esperar medio segundo a que cambie se siente roto.
      this.modoDeAvisos = nuevo
      try {
        await ComentariosService(this.$client).fijarAvisos(
          this.table.id,
          this.row.id,
          nuevo
        )
      } catch (e) {
        this.modoDeAvisos = anterior
      }
    },
    async cargarMiembros() {
      // Si esto falla no se rompe nada: sólo deja de haber menciones. No se
      // pinta error, porque el usuario vino a leer comentarios, no a mencionar.
      try {
        const { data } = await ComentariosService(this.$client).miembros(
          this.table.id
        )
        this.miembros = data
      } catch (e) {
        this.miembros = []
      }
    },

    // ---- menciones -------------------------------------------------------
    alEscribir() {
      const campo = this.$refs.campo
      const hasta = this.borrador.slice(0, campo ? campo.selectionStart : undefined)
      const m = RE_CONSULTA.exec(hasta)
      this.consulta = m ? m[1] : null
      this.indiceActivo = 0
    },
    /**
     * Un solo manejador en vez de modificadores de tecla de Vue.
     *
     * Con `@keydown.down.prevent` las flechas dejarían de mover el cursor
     * dentro del textarea aunque no hubiera desplegable abierto, que es como se
     * escribe el 99% del tiempo. Aquí se corta la tecla SÓLO cuando el
     * desplegable está abierto y de verdad la va a usar.
     */
    alTeclear(e) {
      const abierto = this.sugerencias.length > 0
      const n = this.sugerencias.length

      if (e.key === 'Escape') {
        if (abierto) {
          e.preventDefault()
          this.consulta = null
        }
        return
      }
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && abierto) {
        e.preventDefault()
        const paso = e.key === 'ArrowDown' ? 1 : -1
        this.indiceActivo = (this.indiceActivo + paso + n) % n
        return
      }
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault()
        // Con el desplegable abierto, Enter elige; si no, envía.
        if (abierto) {
          this.elegirMencion(this.sugerencias[this.indiceActivo])
        } else {
          this.enviar()
        }
      }
    },
    elegirMencion(miembro) {
      const campo = this.$refs.campo
      const corte = campo ? campo.selectionStart : this.borrador.length
      const antes = this.borrador.slice(0, corte)
      const despues = this.borrador.slice(corte)
      const m = RE_CONSULTA.exec(antes)
      if (!m) {
        return
      }
      const nuevo = antes.slice(0, m.index) + '@' + miembro.nombre + ' '
      this.borrador = nuevo + despues
      if (!this.mencionados.some((x) => x.id === miembro.id)) {
        this.mencionados.push(miembro)
      }
      this.consulta = null
      this.$nextTick(() => {
        if (campo) {
          campo.focus()
          campo.selectionStart = campo.selectionEnd = nuevo.length
        }
      })
    },
    /**
     * Pasa el texto que se ve ("@Victor Vazquez") al que se guarda ("@{1}").
     *
     * Sólo se convierte a quien se eligió del desplegable. Escribir "@Victor" a
     * mano deja texto literal y no avisa a nadie: el aviso llega porque lo
     * pediste, no porque escribiste un arroba.
     *
     * Se empieza por los nombres más largos para que "Ana" no se coma el
     * principio de "Ana María".
     */
    aTextoGuardado(texto) {
      const usados = []
      const porLargo = [...this.mencionados].sort(
        (a, b) => (b.nombre || '').length - (a.nombre || '').length
      )
      for (const u of porLargo) {
        const marca = '@' + u.nombre
        if (u.nombre && texto.includes(marca)) {
          texto = texto.split(marca).join('@{' + u.id + '}')
          usados.push(u.id)
        }
      }
      return { texto, usados }
    },
    /** Parte el mensaje guardado en trozos, marcando cuáles son menciones. */
    trozos(comentario) {
      const porId = {}
      for (const u of comentario.menciones || []) {
        porId[u.id] = u.nombre
      }
      const fuera = []
      let ultimo = 0
      const texto = comentario.mensaje || ''
      RE_MARCADOR.lastIndex = 0
      let m
      while ((m = RE_MARCADOR.exec(texto)) !== null) {
        if (m.index > ultimo) {
          fuera.push({ texto: texto.slice(ultimo, m.index), mencion: false })
        }
        // Un id que ya no resuelve —la cuenta se borró— no puede quedar como
        // "@{12}" a la vista.
        fuera.push({
          texto: '@' + (porId[Number(m[1])] || 'alguien'),
          mencion: true,
        })
        ultimo = m.index + m[0].length
      }
      if (ultimo < texto.length) {
        fuera.push({ texto: texto.slice(ultimo), mencion: false })
      }
      return fuera
    },

    // ---- enviar y borrar -------------------------------------------------
    async enviar() {
      if (!this.puedeEnviar) {
        return
      }
      this.enviando = true
      this.error = null
      const { texto, usados } = this.aTextoGuardado(this.borrador.trim())
      try {
        const { data } = await ComentariosService(this.$client).crear(
          this.table.id,
          this.row.id,
          texto,
          usados
        )
        this.comentarios.push(data)
        this.borrador = ''
        this.mencionados = []
        this.consulta = null
        this.alFinal()
      } catch (e) {
        this.error = 'No se pudo guardar el comentario.'
      } finally {
        this.enviando = false
      }
    },
    pedirBorrado(comentario) {
      if (this.confirmando !== comentario.id) {
        this.confirmando = comentario.id
        return
      }
      this.borrar(comentario)
    },
    async borrar(comentario) {
      this.confirmando = null
      this.error = null
      try {
        await ComentariosService(this.$client).borrar(comentario.id)
        this.comentarios = this.comentarios.filter(
          (c) => c.id !== comentario.id
        )
      } catch (e) {
        this.error = 'No se pudo borrar el comentario.'
      }
    },

    // ---- reacciones ------------------------------------------------------
    elegirEmoji(comentario, emoji) {
      this.paleta = null
      this.alternarReaccion(comentario, emoji)
    },
    async alternarReaccion(comentario, emoji) {
      const actual = (comentario.reacciones || []).find((r) => r.emoji === emoji)
      const quitar = !!(actual && actual.mio)
      this.error = null
      try {
        const servicio = ComentariosService(this.$client)
        // El servidor devuelve el comentario entero con las cuentas frescas, en
        // vez de que las lleve el frontend: si alguien más reaccionó mientras
        // tanto, el número se corrige solo.
        const { data } = quitar
          ? await servicio.quitarReaccion(comentario.id, emoji)
          : await servicio.reaccionar(comentario.id, emoji)
        const i = this.comentarios.findIndex((c) => c.id === comentario.id)
        if (i !== -1) {
          this.comentarios.splice(i, 1, data)
        }
      } catch (e) {
        this.error = 'No se pudo guardar la reacción.'
      }
    },

    // ---- varios ----------------------------------------------------------
    esMio(comentario) {
      return (
        comentario.usuario != null &&
        comentario.usuario.id === this.usuarioActualId
      )
    },
    autor(comentario) {
      return comentario.usuario?.nombre || 'Usuario eliminado'
    },
    fecha(iso) {
      return new Intl.DateTimeFormat('es-MX', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(iso))
    },
    alFinal() {
      this.$nextTick(() => {
        const lista = this.$refs.lista
        if (lista) {
          lista.scrollTop = lista.scrollHeight
        }
      })
    },
  },
}
</script>

<style scoped>
/*
 * Sin un solo color fijo, a proposito.
 *
 * El CSS de marca se genera pasando el entry.css de Baserow por
 * branding/build-theme.py, que reescribe cada hex que encuentra para el tema
 * oscuro. Como este componente se compila dentro de ese mismo entry.css,
 * cualquier color escrito aqui saldria convertido: un panel claro dentro de una
 * interfaz oscura, o al reves.
 *
 * Heredando el color del contenedor (`inherit`, `currentColor`, `opacity`) el
 * panel se pinta con el tema que este activo, sea claro u oscuro, y el
 * generador no tiene nada que reescribir. Desde que la instancia tiene los dos
 * temas esto dejo de ser una precaucion y paso a ser obligatorio.
 */
.comentarios-mg {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  color: inherit;
}

.comentarios-mg__lista {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 16px;
}

.comentarios-mg__estado {
  padding: 24px 0;
  text-align: center;
  font-size: 13px;
  opacity: 0.6;
}

.comentarios-mg__item {
  padding: 10px 0;
  border-bottom: 1px solid;
  border-bottom-color: color-mix(in srgb, currentColor 18%, transparent);
}

.comentarios-mg__item:last-child {
  border-bottom: 0;
}

.comentarios-mg__cabecera {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
}

.comentarios-mg__autor {
  font-weight: 600;
  font-size: 13px;
}

.comentarios-mg__fecha {
  font-size: 11px;
  opacity: 0.55;
}

.comentarios-mg__borrar {
  margin-left: auto;
  border: 0;
  background: none;
  cursor: pointer;
  color: inherit;
  font-size: 12px;
  line-height: 1;
  padding: 2px 4px;
  opacity: 0.5;
}

.comentarios-mg__borrar:hover {
  opacity: 1;
}

/*
 * Sin justificar, a proposito. La regla de tipografia del proyecto justifica
 * texto largo corrido, pero excluye el copy de UI, y un comentario en una
 * barra de ~250px es justo eso: lineas de cuatro o cinco palabras donde
 * justificar abre huecos enormes entre ellas. Se vio en pantalla.
 */
.comentarios-mg__mensaje {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}

.comentarios-mg__mencion {
  font-weight: 600;
  border-radius: 3px;
  padding: 0 2px;
  background: color-mix(in srgb, currentColor 14%, transparent);
}

/* ---- reacciones ---- */
.comentarios-mg__reacciones {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

.comentarios-mg__reaccion {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border: 1px solid;
  border-color: color-mix(in srgb, currentColor 22%, transparent);
  border-radius: 10px;
  background: none;
  color: inherit;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  padding: 2px 6px;
}

.comentarios-mg__reaccion:hover {
  border-color: currentColor;
}

.comentarios-mg__reaccion--mia {
  border-color: currentColor;
  background: color-mix(in srgb, currentColor 14%, transparent);
}

.comentarios-mg__reaccion-total {
  font-size: 11px;
  opacity: 0.75;
}

/*
 * El "☺+" sólo aparece al pasar el ratón por el comentario. Con ocho
 * comentarios en pantalla, ocho botones siempre visibles convierten la columna
 * en una fila de ruido.
 */
.comentarios-mg__reaccion--agregar {
  opacity: 0;
  border-style: dashed;
}

.comentarios-mg__item:hover .comentarios-mg__reaccion--agregar,
.comentarios-mg__reaccion--agregar:focus {
  opacity: 0.7;
}

.comentarios-mg__paleta {
  position: absolute;
  bottom: 100%;
  left: 0;
  z-index: 2;
  display: flex;
  gap: 2px;
  margin-bottom: 4px;
  padding: 4px;
  border: 1px solid;
  border-color: color-mix(in srgb, currentColor 25%, transparent);
  border-radius: 6px;
  /*
   * Necesita fondo opaco o se lee el comentario de atras a traves de los
   * emojis. `Canvas` es el color de fondo del sistema segun el color-scheme
   * activo, asi que sigue al tema sin escribir un hex —que el generador
   * reescribiria—.
   */
  background: Canvas;
  box-shadow: 0 2px 8px color-mix(in srgb, currentColor 25%, transparent);
}

.comentarios-mg__paleta-boton {
  border: 0;
  background: none;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  padding: 2px;
  border-radius: 4px;
}

.comentarios-mg__paleta-boton:hover {
  background: color-mix(in srgb, currentColor 18%, transparent);
}

/* ---- formulario y menciones ---- */
/* Sin un solo color fijo, como el resto del componente: build-theme.py
   invierte cualquier hex del CSS compilado y saldria al reves en un tema. */
.comentarios-mg__avisos {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px 16px 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  font-size: 12px;
  opacity: 0.65;
  cursor: pointer;
  text-align: left;
}

.comentarios-mg__avisos:hover {
  opacity: 1;
}

.comentarios-mg__form {
  position: relative;
  padding: 12px 16px;
  border-top: 1px solid;
  border-top-color: color-mix(in srgb, currentColor 18%, transparent);
}

.comentarios-mg__sugerencias {
  list-style: none;
  margin: 0 0 6px;
  padding: 4px;
  max-height: 140px;
  overflow-y: auto;
  border: 1px solid;
  border-color: color-mix(in srgb, currentColor 25%, transparent);
  border-radius: 6px;
}

.comentarios-mg__sugerencia {
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}

.comentarios-mg__sugerencia--activa,
.comentarios-mg__sugerencia:hover {
  background: color-mix(in srgb, currentColor 16%, transparent);
}

.comentarios-mg__campo {
  width: 100%;
  resize: vertical;
  border: 1px solid;
  border-color: color-mix(in srgb, currentColor 25%, transparent);
  border-radius: 6px;
  background: transparent;
  color: inherit;
  padding: 8px;
  font: inherit;
  font-size: 13px;
  margin-bottom: 8px;
}

.comentarios-mg__campo:focus {
  outline: none;
  border-color: currentColor;
}
</style>
